import { ValidationError } from '~/errors';
import { fetchInChunks } from '~/utils/batch';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type {
  BulkSendTenantPortalInviteDto,
  CreateTenantPaymentAccountDto,
  HydratedTenantDto,
  HydratedTenantListPaginationDto,
  PaymentAccountDto,
  PaymentAccountsDto,
  PortalInviteResponseDto,
  SendTenantPortalInviteDto,
  TenantAutoPayDto,
  TenantCountsDto,
  TenantCreatedDto,
  TenantDto,
  TransactionPaginationDto,
  UpdateTenantDto,
} from '~/swagger/portal/Api';
import type { ApiFilter, UserQueryFilter } from '~/types/apiFilters';

const TENANTS_URL = '/api/tenants';

function validateTenant(tenant: Partial<HydratedTenantDto | TenantCreatedDto | UpdateTenantDto>): void {
  if (!tenant) throw new ValidationError('Tenant object must be provided');
}

function validateId(tenantId: string): void {
  if (!tenantId) throw new ValidationError(`Tenant ID must be provided`);
}

export type TenantFilter = UserQueryFilter;

export type TenantPaymentFilter = ApiFilter;

export class TenantRepository extends BaseRestRepository {
  public async getTenants(filter?: TenantFilter): Promise<HydratedTenantListPaginationDto> {
    const url = urlWithQuery(TENANTS_URL, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getTenant(tenantId: string): Promise<HydratedTenantDto> {
    validateId(tenantId);
    const url = `${TENANTS_URL}/${tenantId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getCounts(): Promise<TenantCountsDto> {
    const url = `${TENANTS_URL}/counts`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async createTenant(tenant: TenantCreatedDto): Promise<TenantCreatedDto> {
    validateTenant(tenant);
    const url = TENANTS_URL;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(tenant),
    });
    return this.getJsonResponse(response);
  }

  public async updateTenant(tenantId: string, tenant: UpdateTenantDto): Promise<TenantDto> {
    validateId(tenantId);
    validateTenant(tenant);
    const url = `${TENANTS_URL}/${tenantId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(tenant),
    });
    return this.getJsonResponse(response);
  }

  /** Unavailable. use deactivateTenant instead in most cases */
  public async deleteTenant(tenantId: string): Promise<void> {
    validateId(tenantId);
    const url = `${TENANTS_URL}/${tenantId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async deactivateTenant(tenantId: string, forceMoveOutFromLeases: boolean): Promise<void> {
    validateId(tenantId);
    const url = urlWithQuery(`${TENANTS_URL}/${tenantId}/deactivate`, { forceMoveOutFromLeases });
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async portalInvite(tenantId: string, inviteDto: SendTenantPortalInviteDto): Promise<PortalInviteResponseDto> {
    validateId(tenantId);
    const url = `${TENANTS_URL}/${tenantId}/portal-invite`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(inviteDto),
    });
    return this.getJsonResponse(response);
  }

  public async bulkPortalInvite(payload: BulkSendTenantPortalInviteDto): Promise<PortalInviteResponseDto> {
    const url = `${TENANTS_URL}/portal-invite`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async changeTenantPassword(tenantId: string, password: string): Promise<void> {
    validateId(tenantId);
    if (!password) throw new ValidationError('Password must be provided');
    const url = `${TENANTS_URL}/${tenantId}/password`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
    if (!response.ok) {
      console.error(await response.json());
    }
  }

  public async getPayments(tenantId: string, filter?: TenantPaymentFilter): Promise<TransactionPaginationDto> {
    validateId(tenantId);
    const url = urlWithQuery(`${TENANTS_URL}/${tenantId}/payments`, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getAutoPayments(tenantId: string): Promise<TenantAutoPayDto[]> {
    validateId(tenantId);
    const url = `${TENANTS_URL}/${tenantId}/auto-payments`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getPaymentAccounts(tenantId: string): Promise<PaymentAccountsDto> {
    validateId(tenantId);
    const url = `${TENANTS_URL}/${tenantId}/payment-accounts`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async addTenantPaymentAccount(tenantId: string, payload: CreateTenantPaymentAccountDto): Promise<PaymentAccountDto> {
    validateId(tenantId);
    const url = `${TENANTS_URL}/${tenantId}/payment-accounts`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async deleteTenantPaymentAccount(tenantId: string, paymentAccountId: string): Promise<void> {
    validateId(tenantId);
    if (!paymentAccountId) throw new ValidationError('Payment account ID must be provided');
    const url = `${TENANTS_URL}/${tenantId}/payment-accounts/${paymentAccountId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async getTenantsByIds(filter: { tenantIds?: string[] }): Promise<HydratedTenantDto[]> {
    const tenantIds = filter.tenantIds ?? [];
    if (tenantIds.length === 0) {
      const url = urlWithQuery(`${TENANTS_URL}/batch`, filter);
      const response = await this.fetchWithAuth(url);
      return this.getJsonResponse(response);
    }
    return fetchInChunks(tenantIds, async (idsChunk) => {
      const url = urlWithQuery(`${TENANTS_URL}/batch`, { ...filter, tenantIds: idsChunk });
      const response = await this.fetchWithAuth(url);
      return this.getJsonResponse(response);
    });
  }
}

export const tenantRepository = new TenantRepository();
